import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import kitchenMenuLogo from "../../../assets/kitchen-menu.svg";
import { ActionKind } from "../../types/Action";
import { ProductPreset } from "../../types/ProductPreset";
import { translate } from "../../utils/translations";
import { Button } from "../Button";
import { useConfig } from "../ConfigProvider";
import { RootStateKind, useRootState } from "../state/StateProvider";

export const ProductAccessRow = () => {
    const { productPresets } = useConfig();

    const {
        state: { kitchen },
        dispatch
    } = useRootState(RootStateKind.Scan);

    const { i18n } = useTranslation();

    const isQuickAccess = (productPreset: ProductPreset) =>
        productPreset.categories.length === 1 &&
        productPreset.categories[0].products.length === 1;

    if (productPresets.length === 0 && !kitchen.hasKitchen) {
        return null;
    } else {
        return (
            <div
                className="flex gap-2"
                data-testid="productAccessRow"
                role="list"
            >
                {productPresets.map((productPreset) => {
                    return isQuickAccess(productPreset) ? (
                        <ProductAccessRowButton
                            key={productPreset.categories[0].products[0].ean}
                            testId={productPreset.categories[0].products[0].ean}
                            label={translate(
                                productPreset.categories[0].products[0]
                                    .translations,
                                i18n
                            )}
                            image={
                                productPreset.categories[0].products[0]
                                    .imageUrl ?? ""
                            }
                            onClick={() =>
                                dispatch({
                                    kind: ActionKind.AddProduct,
                                    payload: {
                                        kind: "manuallyAdded",
                                        code: productPreset.categories[0]
                                            .products[0].ean,
                                        analyticsSource: "quickAccess"
                                    }
                                })
                            }
                        />
                    ) : (
                        <ProductAccessRowButton
                            key={productPreset.id}
                            testId={productPreset.name}
                            label={translate(productPreset.translations, i18n)}
                            image={productPreset.imageUrl ?? ""}
                            onClick={() =>
                                dispatch({
                                    kind: ActionKind.ShowProducts,
                                    productPreset
                                })
                            }
                        />
                    );
                })}
                {kitchen.hasKitchen && (
                    <ProductAccessRowButton
                        key="kitchenMenu"
                        testId="openKitchenMenu"
                        label=""
                        image={kitchenMenuLogo}
                        onClick={() => {
                            dispatch({
                                kind: ActionKind.ShowKitchenMenu
                            });
                        }}
                        disabled={!kitchen.menu}
                        className="border-[#025F69] bg-[#025F69]"
                    />
                )}
            </div>
        );
    }
};

const ProductAccessRowButton = ({
    testId,
    label,
    image,
    onClick,
    disabled,
    className
}: {
    testId: string;
    label: string;
    image: string;
    onClick: () => void;
    disabled?: boolean;
    className?: string;
}) => {
    return (
        <Button
            variant="subtle"
            role="listitem"
            data-testid={testId}
            onClick={onClick}
            disabled={disabled}
            className={twMerge(
                `flex-1 place-items-center overflow-hidden p-0 text-center text-xs`,
                className
            )}
        >
            <img src={image} className="mx-auto h-16 w-auto object-contain" />
            {label}
        </Button>
    );
};
